import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Lock, ShieldAlert } from 'lucide-react';
import {
  useMyProfile,
  useChangeMyPassword,
  profileKeys,
} from '@/hooks/useMyProfile';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { TextInput } from '@/components/ui/text-input';

/**
 * Blocks the authenticated app behind a mandatory change-password modal
 * when the user is on an owner-issued temporary password
 * (`mustChangePassword`). The user enters that temp password as the
 * "current" one plus a new password; success clears the flag server-side
 * and we refetch /auth/me to release the gate.
 *
 * Rendered once at the app shell, above everything, and non-dismissable —
 * there's no close button and the backdrop doesn't dismiss.
 */
export const ForcePasswordChangeGate: React.FC = () => {
  const { t, isRTL } = useTranslation();
  const { data: profile } = useMyProfile();
  const changePassword = useChangeMyPassword();
  const qc = useQueryClient();

  const [current, setCurrent] = React.useState('');
  const [next, setNext] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  if (!profile?.mustChangePassword) return null;

  const nextValid = next.length >= 8;
  const matches = next === confirm;
  const differs = next !== current;
  const canSubmit =
    !!current && nextValid && matches && differs && !changePassword.isPending;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    try {
      await changePassword.mutateAsync({
        currentPassword: current,
        newPassword: next,
      });
      toast.success(t('auth.reset.successToast'));
      // Releases the gate — /auth/me now returns mustChangePassword=false.
      await qc.invalidateQueries({ queryKey: profileKeys.me });
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const anyErr = err as any;
      const code: string | undefined =
        anyErr?.response?.data?.code ?? anyErr?.response?.data?.error?.code;
      if (code === 'INVALID_CURRENT_PASSWORD') {
        setError(t('auth.reset.tempWrong'));
      } else if (code === 'PASSWORD_UNCHANGED') {
        setError(t('auth.reset.mustDiffer'));
      } else {
        setError(
          anyErr?.response?.data?.message || t('auth.reset.genericError'),
        );
      }
    }
  };

  return (
    <div
      dir={isRTL ? 'rtl' : 'ltr'}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-sm rounded-2xl p-6"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          border: '1px solid var(--color-border-primary)',
        }}
      >
        <div className="flex items-center gap-2 mb-3">
          <ShieldAlert
            className="w-5 h-5"
            style={{ color: 'var(--color-warning, #d97706)' }}
          />
          <h2
            className="text-lg font-semibold"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {t('auth.reset.mustChangeTitle')}
          </h2>
        </div>
        <p
          className="text-sm mb-4"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          {t('auth.reset.mustChangeBody')}
        </p>
        <form onSubmit={submit} className="space-y-3">
          <Field
            label={t('auth.reset.currentTempLabel')}
            value={current}
            onChange={setCurrent}
            placeholder={t('auth.reset.currentTempPlaceholder')}
            autoComplete="current-password"
            isRTL={isRTL}
            autoFocus
          />
          <Field
            label={t('auth.reset.newPasswordLabel')}
            value={next}
            onChange={setNext}
            placeholder={t('auth.reset.newPasswordPlaceholder')}
            autoComplete="new-password"
            isRTL={isRTL}
            hint={
              next.length > 0 && !nextValid ? t('auth.reset.minLength') : undefined
            }
          />
          <Field
            label={t('auth.reset.confirmLabel')}
            value={confirm}
            onChange={setConfirm}
            autoComplete="new-password"
            isRTL={isRTL}
            hint={
              confirm.length > 0 && !matches ? t('auth.reset.noMatch') : undefined
            }
          />
          {error && (
            <div
              className="text-xs rounded-lg px-3 py-2"
              style={{
                backgroundColor:
                  'color-mix(in srgb, var(--color-error) 10%, var(--color-bg-primary))',
                color: 'var(--color-error)',
                border: '1px solid var(--color-error)',
              }}
            >
              {error}
            </div>
          )}
          <Button
            type="submit"
            size="lg"
            block
            loading={changePassword.isPending}
            disabled={!canSubmit}
          >
            {t('auth.reset.setPasswordButton')}
          </Button>
        </form>
      </div>
    </div>
  );
};

const Field: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoComplete?: string;
  isRTL: boolean;
  autoFocus?: boolean;
  hint?: string;
}> = ({ label, value, onChange, placeholder, autoComplete, isRTL, autoFocus, hint }) => (
  <div>
    <label
      className="block text-xs font-semibold mb-1"
      style={{ color: 'var(--color-text-secondary)' }}
    >
      {label}
    </label>
    <div className="relative">
      <Lock
        className="absolute top-1/2 -translate-y-1/2 w-4 h-4"
        style={{
          color: 'var(--color-text-tertiary)',
          [isRTL ? 'right' : 'left']: 12,
        }}
      />
      <TextInput
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        required
        className={isRTL ? 'pr-9 pl-3' : 'pl-9 pr-3'}
      />
    </div>
    {hint && (
      <p className="text-[11px] mt-1" style={{ color: 'var(--color-error)' }}>
        {hint}
      </p>
    )}
  </div>
);

export default ForcePasswordChangeGate;
